/* eslint-disable camelcase */
import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getBrandValue } from '~/shared/utils/brandNameHelper';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { CUSTOM_COLOR_FILTER } from '../../constants';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  getSelectedOptionCount,
  isCioColorFacet,
  isProductsV2ColorFacet,
  sanitizeFilterName,
} from '../../utils/filters';
import AnimatedDisclosurePanel from '../SharedGridFiltersUi/AnimatedDisclosurePanel';
import BrandedToggleIcon from '../SharedGridFiltersUi/BrandedToggleIcon';

const DynamicFormattedMessage = FormattedMessage;

const DesktopGridFilterGroupDisclosureContainer = styled.div`
  font-size: 0.875rem;
`;

const DesktopGridFilterGroupDisclosureToggleButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.75rem 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  font-family: inherit;

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) =>
        theme.colors
          .gridFilterGroupDisclosureToggleButtonFocusVisibleOutlineColor};
    outline-offset: 2px;
  }
  ${({ theme }) => theme.gridFilterStyles.filterCategoryTitleStyle};
`;

const SelectedCount = styled.span`
  color: ${({ theme }) =>
    theme.colors.gridFilterGroupDisclosurePanelWrapperSelectedCountTextColor};
  font-size: 0.875rem;
`;

function DesktopGridFilterGroupDisclosure({
  display_name,
  sub_facets,
  name,
  options,
  children,
  togglePanel,
  openPanels,
}) {
  const sanitizedFacetName = slugify(name, '-');
  const panelId = `desktop-disclosure-panel-${sanitizedFacetName}`;
  const sanitizedName = sanitizeFilterName(name);
  const isOpen = openPanels[panelId] || false;
  const { selectedUrlFilters, customFacetFilters } =
    useUnifiedProductGridData();
  const numSelected = getSelectedOptionCount({
    name,
    subFacets: sub_facets,
    options,
    selectedUrlFilters,
  });

  const isColorFacet = isCioColorFacet(name) || isProductsV2ColorFacet(name);
  const appliedCustomColor = customFacetFilters[CUSTOM_COLOR_FILTER.name];
  const hasCustomColorSelected =
    isColorFacet && !!appliedCustomColor && !appliedCustomColor.hasError;
  const effectiveNumSelected = numSelected + (hasCustomColorSelected ? 1 : 0);

  return (
    <DesktopGridFilterGroupDisclosureContainer>
      <DesktopGridFilterGroupDisclosureToggleButton
        aria-expanded={isOpen}
        aria-controls={panelId}
        id={panelId + '-label'}
        onClick={() => togglePanel(panelId)}
        data-autotag={`filter-${sanitizedName}`}
      >
        <div>
          <DynamicFormattedMessage
            id={`site_product_filter.${slugify(
              getBrandValue({
                savage: name,
                default: display_name,
              })
            )}`}
            defaultMessage={display_name}
          />
          {effectiveNumSelected > 0 && (
            <SelectedCount> ({effectiveNumSelected})</SelectedCount>
          )}
        </div>
        <BrandedToggleIcon open={isOpen} name={sanitizedName} />
      </DesktopGridFilterGroupDisclosureToggleButton>

      <AnimatedDisclosurePanel
        open={isOpen}
        panelId={panelId}
        viewport="desktop"
      >
        {children}
      </AnimatedDisclosurePanel>
    </DesktopGridFilterGroupDisclosureContainer>
  );
}

DesktopGridFilterGroupDisclosure.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  display_name: PropTypes.string.isRequired,
  name: PropTypes.string,
  openPanels: PropTypes.objectOf(PropTypes.bool).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  sub_facets: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      name: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          display_name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    })
  ),
  togglePanel: PropTypes.func.isRequired,
};

export default DesktopGridFilterGroupDisclosure;
